<template>
  <ResourceDialog
    @close="close"
    @save="createPhone"
    ref="phoneDialog"
    :cancel-button-text="cancelButtonText"
    :max-width="600"
    :processing="processing"
    :save-button-text="saveButtonText"
    :title="title"
  >
    <template #form>
      <v-row dense>
        <v-col>
          <p class="fs-16 mb-0">
            {{
              $bct(
                'May we send you text messages? If yes, please enter your phone number. You will receive a text to verify this number.',
              )
            }}
          </p>
        </v-col>
      </v-row>
      <v-row>
        <LabeledTextfield
          v-model="phone"
          mask="(###) ###-####"
          message="Cell phone number"
        />
        <v-col>
          <v-checkbox
            v-model="textOptedIn"
            :label="
              $bct(
                'I consent to receive communication by text message to the cellphone number provided above.',
              )
            "
            class="mt-0 label-opacity-override"
            hide-details
          />
        </v-col>
      </v-row>

      <template v-if="!disablePhoneSkip">
        <v-row>
          <v-col class="d-flex justify-center">
            <v-btn
              @click="neverShowPhonePrompt()"
              color="primary"
              size="small"
              variant="outlined"
            >
              <span>
                {{ $bct('Skip and never ask me again') }}
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </template>
  </ResourceDialog>

  <ResourceDialog
    ref="phoneCodeDialog"
    :max-width="600"
    :processing="processing"
    cancel-button-text="Back"
    save-button-text="Submit"
    title="Confirm your phone number"
  >
    <template #form>
      <v-row>
        <v-col cols="12">
          <p>
            {{ $bct('Enter the confirmation code sent to your phone.') }}
          </p>
        </v-col>
        <v-col cols="12">
          <v-otp-input
            v-model="code"
            @finish="checkCode"
            :disabled="processing"
            class="mb-4"
            length="6"
          />
        </v-col>
      </v-row>
    </template>
  </ResourceDialog>
</template>

<script setup>
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import { trimOtp } from '@/shared/utils/otp.js';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');
const { api } = useApi();
const eventBus = useEventBus();

defineProps({
  cancelButtonText: {
    type: String,
    default: null,
  },
  saveButtonText: {
    type: String,
    default: null,
  },
  disablePhoneSkip: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: 'Add your phone number',
  },
});

const emit = defineEmits(['cancel', 'change', 'close']);

const phone = ref(null);
const confirmed = ref(false);
const code = ref(null);
const processing = ref(false);
const textOptedIn = ref(false);
const phoneDialog = ref(null);
const phoneCodeDialog = ref(null);

watch(code, (newValue) => trimOtp(code, newValue));

function close() {
  if (confirmed.value) {
    return emit('close');
  }
  return emit('cancel');
}

async function createPhone() {
  if (!(phone.value && textOptedIn.value))
    return eventBus.chime('You must consent to receive text messages at this number to proceed');

  processing.value = true;
  const resp = await api.member.phone
    .create({ number: phone.value })
    .catch((error) => eventBus.error(error));
  processing.value = false;
  if (resp.status !== 201) return false;

  phoneCodeDialog.value?.open({});
  return true;
}

async function checkCode() {
  processing.value = true;
  const resp = await api.member.phone
    .update({ code: code.value })
    .catch((error) => eventBus.error(error));
  processing.value = false;
  if (resp.status !== 200) return;
  confirmed.value = true;
  eventBus.chime('Saved');
  phoneDialog.value.close();
  phoneCodeDialog.value.close();
  emit('change');
}

async function neverShowPhonePrompt() {
  processing.value = true;
  const resp = await api.member.phone.skipPhoneDialog().catch((error) => eventBus.error(error));
  processing.value = false;
  if (resp.status !== 200) return;
  confirmed.value = true;
  eventBus.chime('Saved');
  phoneDialog.value.close();
  phoneCodeDialog.value.close();
  emit('change');
}

function open() {
  phone.value = null;
  textOptedIn.value = null;
  code.value = null;
  return phoneDialog.value.open();
}

defineExpose({ open });
</script>

<style scoped>
.label-opacity-override:deep(.v-label) {
  opacity: 1;
}
</style>
